export interface ReplayState {
  tick: number;
  maxTick: number;
  playing: boolean;
  speed: number; // multiplier over the 4 ticks/sec base rate
  focus: number | null; // the followed agent, or null
}

export type ReplayEvent =
  | { type: "loaded"; maxTick: number }
  | { type: "seek"; tick: number }
  | { type: "step"; delta: number }
  | { type: "play" }
  | { type: "pause" }
  | { type: "toggle" }
  | { type: "speed"; speed: number }
  | { type: "advance" }
  | { type: "focus"; agent: number }
  | { type: "clearFocus" };

export const initialReplayState: ReplayState = { tick: 0, maxTick: 0, playing: false, speed: 1, focus: null };

export function clampTick(tick: number, maxTick: number): number {
  if (maxTick < 1) return 0;
  return Math.max(1, Math.min(maxTick, Math.round(tick)));
}

export function replayReducer(state: ReplayState, event: ReplayEvent): ReplayState {
  switch (event.type) {
    case "loaded":
      return { ...state, maxTick: event.maxTick, tick: event.maxTick ? 1 : 0, playing: false, focus: null };
    case "seek":
      return { ...state, tick: clampTick(event.tick, state.maxTick) };
    case "step":
      return { ...state, playing: false, tick: clampTick(state.tick + event.delta, state.maxTick) };
    case "play":
      if (state.maxTick < 1) return state;
      // Pressing play at the end restarts from the first tick.
      return { ...state, playing: true, tick: state.tick >= state.maxTick ? 1 : state.tick };
    case "pause":
      return { ...state, playing: false };
    case "toggle":
      return replayReducer(state, { type: state.playing ? "pause" : "play" });
    case "speed":
      return { ...state, speed: event.speed };
    case "advance":
      if (!state.playing) return state;
      if (state.tick >= state.maxTick) return { ...state, playing: false };
      return { ...state, tick: state.tick + 1 };
    case "focus":
      return { ...state, focus: event.agent };
    case "clearFocus":
      return { ...state, focus: null };
  }
}
